import React from 'react';
import { Link } from 'react-router-dom';
import { Activity, Gauge, TrendingUp, ShieldCheck, ArrowRight, ScanLine } from 'lucide-react';
import { InlineTrainingQuiz } from './InlineTrainingQuiz';
import { useAIFormCheckVisibility } from '../../hooks/useAIFormCheckVisibility';

/** Section 3 — Free Running Performance Assessment (Running Potential Report) + inline plan quiz. */
const POINTS = [
  { Icon: Gauge, text: 'Your current pace, speed and estimated race-equivalent times for 3 km to the marathon' },
  { Icon: TrendingUp, text: 'Conservative 3-, 6- and 12-month improvement scenarios based on your training background' },
  { Icon: Activity, text: 'Coach-style insights on endurance, pacing and your top three training priorities' },
];

export const AssessmentSpotlightSection = () => {
  const { visible: aiVisible } = useAIFormCheckVisibility();
  return (
    <section id="online-training-plan" className="relative overflow-hidden py-16 md:py-20 border-y border-[#EDE9FE] bg-gradient-to-b from-[#FAF5FF] via-white to-[#F5F3FF] scroll-mt-20" data-testid="assessment-spotlight-section" aria-labelledby="assessment-heading">
      <div className="absolute -top-20 -left-16 w-72 h-72 rounded-full bg-[#A855F7]/10 blur-3xl pointer-events-none" aria-hidden />
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="mb-10 text-center">
          <div className="text-[10px] uppercase tracking-widest text-[#A855F7] font-black mb-2">Free · takes about 3 minutes</div>
          <h2 id="assessment-heading" className="text-3xl md:text-5xl font-black tracking-tight text-[#0F172A]">Free Running Performance Assessment</h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">Tell us about a recent run and your training week. We calculate where you stand today and what a realistic next 12 months could look like — then recommend the right LGRA plan.</p>
        </div>
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
          <div className="rounded-2xl bg-white border border-[#EDE9FE] p-6 md:p-8 flex flex-col" style={{ boxShadow: '0 8px 32px -12px rgba(168, 85, 247, 0.15)' }} data-testid="assessment-card">
            <h3 className="text-xl font-black tracking-tight text-[#0F172A] mb-4">What your Running Potential Report includes</h3>
            <ul className="space-y-3 flex-1">
              {POINTS.map(({ Icon, text }) => (
                <li key={text} className="flex items-start gap-3 text-sm text-slate-700"><span className="w-8 h-8 rounded-lg bg-[#F5F3FF] text-[#7E22CE] flex items-center justify-center shrink-0"><Icon size={16} /></span><span>{text}</span></li>
              ))}
            </ul>
            <p className="mt-5 flex items-start gap-2 text-xs text-slate-500"><ShieldCheck size={14} className="text-[#0D9488] shrink-0 mt-0.5" /> Numbers are calculated from the results you enter — never invented. Your data stays private to you and LGRA coaches.</p>
            <Link to="/online-training-plan" className="mt-5 inline-flex items-center justify-center gap-2 w-full h-12 rounded-md bg-gradient-to-r from-[#A855F7] to-[#7E22CE] hover:from-[#9333EA] hover:to-[#6B21A8] text-white font-black uppercase tracking-widest text-xs shadow-md" data-testid="assessment-cta">Start my free assessment <ArrowRight size={14} /></Link>
            {aiVisible && (
              <Link to="/ai-running-form-check" className="mt-3 inline-flex items-center justify-center gap-1.5 w-full text-xs font-bold text-[#7E22CE] hover:text-[#4C1D95] underline underline-offset-4 hover:no-underline" data-testid="assessment-ai-form-check">
                <ScanLine size={12} /> Also try the AI Running Form Check
              </Link>
            )}
          </div>
          <InlineTrainingQuiz />
        </div>
      </div>
    </section>
  );
};

export default AssessmentSpotlightSection;
